import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { runsAPI } from '../utils/api';
import { useWebSocket } from '../hooks/useWebSocket';
import StatusBadge from '../components/StatusBadge';
import ProjectSteps from '../components/ProjectSteps';
import LogViewer from '../components/LogViewer';
import FileBrowser from '../components/FileBrowser';
import GitHistory from '../components/GitHistory';
import DiffViewer from '../components/DiffViewer';
import SplitLayout from '../components/SplitLayout';
import PreviewPanel from '../components/PreviewPanel';
import ChatPanel from '../components/ChatPanel';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft,
  Clock,
  DollarSign,
  Download,
  Layers,
  Loader2,
  MessageSquare,
  XCircle,
  RotateCcw,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [steps, setSteps] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [validationDialog, setValidationDialog] = useState(null);
  const [validationFeedback, setValidationFeedback] = useState('');
  const [bottomPanelOpen, setBottomPanelOpen] = useState(true);

  // Charger les données initiales
  useEffect(() => {
    loadRun();
  }, [id]);

  const loadRun = async () => {
    try {
      const res = await runsAPI.get(id);
      const data = res.data;
      setRun(data);
      setSteps(data.steps || []);
      setLogs(data.logs || []);
    } catch (err) {
      toast.error('Impossible de charger le projet');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  // WebSocket pour les mises à jour en temps réel
  const onLog = useCallback((data) => {
    setLogs((prev) => [...prev, data]);
  }, []);

  const onStepUpdate = useCallback((data) => {
    setSteps((prev) => {
      const idx = prev.findIndex(
        (s) => s.step_number === data.step_number || s.id === data.id
      );
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], ...data };
        return updated;
      }
      return [...prev, data];
    });
    if (data.step_number !== undefined) {
      setRun((prev) => prev ? { ...prev, current_step: data.step_number } : prev);
    }
  }, []);

  const onStatusUpdate = useCallback((data) => {
    setRun((prev) => prev ? { ...prev, status: data.status } : prev);
    if (data.status === 'completed') {
      toast.success('Projet terminé !');
    } else if (data.status === 'failed') {
      toast.error('Le projet a échoué');
    }
  }, []);

  const onValidation = useCallback((data) => {
    setRun((prev) => prev ? { ...prev, status: 'awaiting_validation' } : prev);
    setValidationDialog(data);
    toast.info('Validation requise', { duration: 10000 });
  }, []);

  const { connected } = useWebSocket(id, {
    onLog,
    onStepUpdate,
    onStatusUpdate,
    onValidation,
  });

  // Actions
  const handleCancel = async () => {
    try {
      await runsAPI.cancel(id);
      toast.info('Annulation demandée');
      loadRun();
    } catch {
      toast.error("Impossible d'annuler");
    }
  };

  const handleValidatePlan = async (approved) => {
    try {
      await runsAPI.validatePlan(id, approved, '');
      toast.success(approved ? 'Plan approuvé' : 'Plan rejeté');
      loadRun();
    } catch {
      toast.error('Erreur de validation');
    }
  };

  const loadConversations = async () => {
    try {
      const res = await runsAPI.getConversations(id);
      setConversations(res.data.conversations || res.data || []);
    } catch {
      toast.error('Impossible de charger les conversations');
    }
  };

  const handleStepValidation = async (approved) => {
    try {
      await runsAPI.validateStep(id, approved, validationFeedback);
      toast.success(approved ? 'Étape approuvée' : 'Étape rejetée');
      setValidationDialog(null);
      setValidationFeedback('');
      loadRun();
    } catch {
      toast.error('Erreur de validation');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!run) return null;

  const isRunning = run.status === 'running';
  const isAwaitingValidation = run.status === 'awaiting_validation';

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] gap-2">
      {/* Header — compact */}
      <div className="flex items-center justify-between gap-3 flex-shrink-0 px-1">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => navigate('/projects')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <StatusBadge status={run.status} />
          {connected && isRunning && (
            <Badge variant="outline" className="text-[10px] text-emerald-400 border-emerald-400/30">
              Live
            </Badge>
          )}
          <h2 className="text-sm font-medium text-foreground truncate">{run.goal}</h2>
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground flex-shrink-0">
            <Badge variant="outline" className="text-[10px] capitalize">{run.stack}</Badge>
            <span className="flex items-center gap-0.5">
              <Layers className="w-3 h-3" />
              {run.current_step || 0}/{run.max_steps || 20}
            </span>
            {run.cost_used_eur > 0 && (
              <span className="flex items-center gap-0.5">
                <DollarSign className="w-3 h-3" />
                {run.cost_used_eur.toFixed(4)}
              </span>
            )}
            <span className="flex items-center gap-0.5">
              <Clock className="w-3 h-3" />
              {run.created_at
                ? formatDistanceToNow(new Date(run.created_at), { addSuffix: true, locale: fr })
                : '-'}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          {isAwaitingValidation && (
            <>
              <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => handleValidatePlan(false)}>
                <XCircle className="w-3 h-3 mr-1" />
                Rejeter
              </Button>
              <Button size="sm" className="h-7 text-xs glow-primary" onClick={() => handleValidatePlan(true)}>
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Approuver
              </Button>
            </>
          )}
          {isRunning && (
            <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={handleCancel}>
              <XCircle className="w-3 h-3 mr-1" />
              Annuler
            </Button>
          )}
          {(run.status === 'failed' || run.status === 'cancelled') && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => navigate('/projects/new')}>
              <RotateCcw className="w-3 h-3 mr-1" />
              Relancer
            </Button>
          )}
          {run.status === 'completed' && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={async () => {
              try {
                const res = await runsAPI.export(id);
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a');
                a.href = url;
                a.download = `project-${id}.zip`;
                a.click();
                window.URL.revokeObjectURL(url);
                toast.success('Export téléchargé');
              } catch { toast.error("Erreur d'export"); }
            }}>
              <Download className="w-3 h-3 mr-1" />
              Export
            </Button>
          )}
        </div>
      </div>

      {/* Step Validation Dialog */}
      {validationDialog && (
        <Card className="border-amber-500/50 bg-amber-500/5 flex-shrink-0">
          <CardHeader className="pb-2 py-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-amber-500" />
              Validation requise
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 pb-3">
            <p className="text-xs text-muted-foreground">
              {validationDialog.message || "L'agent attend votre approbation pour continuer."}
            </p>
            <textarea
              className="w-full p-2 text-xs border border-border rounded-md bg-background resize-none"
              rows={2}
              placeholder="Feedback optionnel..."
              value={validationFeedback}
              onChange={(e) => setValidationFeedback(e.target.value)}
            />
            <div className="flex gap-2">
              <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => handleStepValidation(false)}>
                <XCircle className="w-3 h-3 mr-1" />
                Rejeter
              </Button>
              <Button size="sm" className="h-7 text-xs glow-primary" onClick={() => handleStepValidation(true)}>
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Approuver
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Triptyque: Code | Preview | Chat */}
      <div className="flex-1 min-h-0">
        <SplitLayout
          className="h-full"
          leftPanel={<FileBrowser projectId={id} />}
          centerPanel={<PreviewPanel projectId={id} stack={run.stack} />}
          rightPanel={<ChatPanel projectId={id} stack={run.stack} currentRunGoal={run.goal} />}
        />
      </div>

      {/* Bottom panel: Tabs (collapsible) */}
      <div className={`flex-shrink-0 border border-border rounded-lg overflow-hidden transition-all ${bottomPanelOpen ? 'h-64' : 'h-8'}`}>
        <Tabs defaultValue="steps" className="h-full flex flex-col">
          <div className="flex items-center justify-between bg-muted/30 border-b border-border h-8 px-2 flex-shrink-0">
            <TabsList className="bg-transparent h-7 p-0">
              <TabsTrigger value="steps" className="text-xs h-6 px-2">
                Étapes ({steps.length})
              </TabsTrigger>
              <TabsTrigger value="logs" className="text-xs h-6 px-2">
                Logs ({logs.length})
              </TabsTrigger>
              <TabsTrigger value="git" className="text-xs h-6 px-2">
                Git
              </TabsTrigger>
              <TabsTrigger value="diffs" className="text-xs h-6 px-2">
                Diffs
              </TabsTrigger>
              <TabsTrigger value="agents" className="text-xs h-6 px-2" onClick={loadConversations}>
                <MessageSquare className="w-3 h-3 mr-1" />
                Agents
              </TabsTrigger>
            </TabsList>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={() => setBottomPanelOpen(!bottomPanelOpen)}
            >
              {bottomPanelOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />}
            </Button>
          </div>

          {bottomPanelOpen && (
            <div className="flex-1 overflow-auto">
              <TabsContent value="steps" className="mt-0 p-2 h-full">
                <ProjectSteps steps={steps} currentStep={run.current_step} />
              </TabsContent>
              <TabsContent value="logs" className="mt-0 h-full">
                <LogViewer logs={logs} />
              </TabsContent>
              <TabsContent value="git" className="mt-0 h-full">
                <GitHistory projectId={id} />
              </TabsContent>
              <TabsContent value="diffs" className="mt-0 h-full">
                <DiffViewer run={run} />
              </TabsContent>
              <TabsContent value="agents" className="mt-0 p-2 h-full">
                {conversations.length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-4">Aucune conversation d'agent disponible</p>
                ) : (
                  <div className="space-y-2">
                    {conversations.map((conv, idx) => (
                      <div key={idx} className="border border-border rounded-lg p-2">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline" className="text-[10px]">{conv.agent || conv.role || `Agent ${idx + 1}`}</Badge>
                          {conv.model && <Badge variant="secondary" className="text-[10px]">{conv.model}</Badge>}
                        </div>
                        <div className="space-y-1">
                          {(conv.messages || []).map((msg, mIdx) => (
                            <div key={mIdx} className={`p-1.5 rounded text-[10px] font-mono ${msg.role === 'assistant' ? 'bg-muted/50' : 'bg-primary/5'}`}>
                              <span className="font-semibold">{msg.role}: </span>
                              <span className="whitespace-pre-wrap">{typeof msg.content === 'string' ? msg.content.slice(0, 300) : JSON.stringify(msg.content).slice(0, 300)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </div>
          )}
        </Tabs>
      </div>
    </div>
  );
}
